import React from "react";
import { DeletePopUp } from "../../../../components/PopUp";
import { Box, Typography, CircularProgress } from "@mui/material";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import PaymentIcon from "@mui/icons-material/Payment";
import { DeleteLiabilityModalProps } from "./types";

export const DeleteLiabilityModal: React.FC<DeleteLiabilityModalProps> =
  React.memo(
    ({
      isOpen,
      liabilityName,
      onClose,
      onDelete,
      deletionImpact,
      isLoadingImpact = false,
      isDeleting = false,
      deleteError,
    }) => {
      const renderImpactDetails = () => {
        if (isLoadingImpact) {
          return (
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, my: 2 }}
              role="status"
              aria-label="Loading deletion impact information"
            >
              <CircularProgress size={16} aria-label="Loading" />
              <Typography variant="body2" color="text.secondary">
                Loading deletion impact...
              </Typography>
            </Box>
          );
        }

        if (!deletionImpact) {
          return null;
        }

        const {
          totalTransactions,
          expenseCount,
          incomeCount,
          transferCount,
          loanPaymentCount,
        } = deletionImpact;

        if (totalTransactions === 0) {
          return (
            <Box
              sx={{ my: 2, p: 1.5, bgcolor: "success.50", borderRadius: 1 }}
              role="status"
              aria-label="No transactions affected"
            >
              <Typography variant="body2" color="success.dark">
                ✓ No transactions will be affected by this deletion.
              </Typography>
            </Box>
          );
        }

        return (
          <Box
            sx={{ my: 2, p: 1.5, bgcolor: "warning.50", borderRadius: 1 }}
            role="alert"
            aria-label={`Warning: ${totalTransactions} transactions will be affected`}
          >
            <Typography
              variant="subtitle2"
              color="warning.dark"
              sx={{ mb: 1, fontWeight: 600 }}
            >
              ⚠️ {totalTransactions} transaction
              {totalTransactions > 1 ? "s" : ""} will be permanently impacted or
              deleted:
            </Typography>
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 0.5, ml: 1 }}
              role="list"
              aria-label="Transaction impact breakdown"
            >
              {expenseCount > 0 && (
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  role="listitem"
                  aria-label={`${expenseCount} expense transaction${expenseCount > 1 ? "s" : ""} will be kept but moved to hidden account`}
                >
                  <TrendingDownIcon
                    sx={{ fontSize: 16, color: "error.main" }}
                    aria-hidden="true"
                  />
                  <Typography variant="body2">
                    {expenseCount} expense{expenseCount > 1 ? "s" : ""}
                  </Typography>
                </Box>
              )}
              {incomeCount > 0 && (
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  role="listitem"
                  aria-label={`${incomeCount} income transaction${incomeCount > 1 ? "s" : ""} will be kept but moved to hidden account`}
                >
                  <TrendingUpIcon
                    sx={{ fontSize: 16, color: "success.main" }}
                    aria-hidden="true"
                  />
                  <Typography variant="body2">
                    {incomeCount} income transaction{incomeCount > 1 ? "s" : ""}
                  </Typography>
                </Box>
              )}
              {transferCount > 0 && (
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  role="listitem"
                  aria-label={`${transferCount} transfer transaction${transferCount > 1 ? "s" : ""} will be kept but moved to hidden account`}
                >
                  <SwapHorizIcon
                    sx={{ fontSize: 16, color: "info.main" }}
                    aria-hidden="true"
                  />
                  <Typography variant="body2">
                    {transferCount} transfer{transferCount > 1 ? "s" : ""}
                  </Typography>
                </Box>
              )}
              {loanPaymentCount > 0 && (
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  role="listitem"
                  aria-label={`${loanPaymentCount} loan payment transaction${loanPaymentCount > 1 ? "s" : ""} will be affected`}
                >
                  <PaymentIcon
                    sx={{ fontSize: 16, color: "secondary.main" }}
                    aria-hidden="true"
                  />
                  <Typography variant="body2">
                    {loanPaymentCount} loan payment
                    {loanPaymentCount > 1 ? "s" : ""}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        );
      };

      return (
        <DeletePopUp
          isOpen={isOpen}
          setIsOpen={onClose}
          title="Delete Liability"
          text={
            <>
              Do you really want to delete the <strong>{liabilityName}</strong>{" "}
              liability?
              {renderImpactDetails()}
            </>
          }
          handleOnDelete={onDelete}
          isLoading={isDeleting}
          errorMessage={deleteError}
        />
      );
    },
  );

DeleteLiabilityModal.displayName = "DeleteLiabilityModal";
